import React from 'react';
import { TYPE_PERCENTAGE } from './couponAdapter';
import { STATUS_STYLES, daysUntilExpiry, isExpiringSoon } from './couponStatus';

/** The small read-only pieces shared by the table and the mobile card list. */

export const StatusChip = ({ status }) => {
    const styles = STATUS_STYLES[status];
    return (
        <span
            className={`cpn-inline-flex cpn-items-center cpn-gap-1.5 cpn-rounded-full cpn-px-2.5 cpn-py-1 cpn-text-xs cpn-font-semibold ${styles.chip}`}
        >
            <span className={`cpn-h-1.5 cpn-w-1.5 cpn-rounded-full ${styles.dot}`} aria-hidden="true" />
            {status}
        </span>
    );
};

export const ExpiringChip = ({ coupon }) => {
    if (!isExpiringSoon(coupon)) return null;
    const days = daysUntilExpiry(coupon);

    return (
        <span className="cpn-inline-flex cpn-items-center cpn-rounded-full cpn-bg-orange-50 cpn-px-2 cpn-py-0.5 cpn-text-[11px] cpn-font-semibold cpn-text-orange-700">
            {days === 0 ? 'Ends today' : days === 1 ? 'Ends tomorrow' : `Ends in ${days} days`}
        </span>
    );
};

export const TypeChip = ({ type }) => (
    <span
        className={`cpn-inline-flex cpn-items-center cpn-rounded-full cpn-px-2.5 cpn-py-1 cpn-text-xs cpn-font-semibold ${
            type === TYPE_PERCENTAGE
                ? 'cpn-bg-violet-50 cpn-text-violet-700'
                : 'cpn-bg-sky-50 cpn-text-sky-700'
        }`}
    >
        {type}
    </span>
);

/**
 * Redemptions against the cap.
 *
 * `possibleUsage` is null for coupons with no limit — either never set or a legacy 0, both
 * normalised in couponAdapter — and those get the count without a bar, since there is no
 * proportion to show.
 */
export const UsageMeter = ({ coupon }) => {
    const limit = coupon.possibleUsage;
    const percent = limit ? Math.min(100, Math.round((coupon.redeemed / limit) * 100)) : 0;

    return (
        <div>
            <p className="cpn-tabular-nums cpn-text-neutral-800">
                <span className="cpn-font-semibold">{coupon.redeemed}</span>
                <span className="cpn-text-neutral-400"> of {limit === null ? '∞' : limit} used</span>
            </p>
            {limit !== null && (
                <div
                    className="cpn-mt-2 cpn-h-1.5 cpn-w-32 cpn-overflow-hidden cpn-rounded-full cpn-bg-neutral-200"
                    role="progressbar"
                    aria-valuenow={percent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`${coupon.name} usage`}
                >
                    <div
                        className={`cpn-h-full cpn-rounded-full cpn-transition-all ${
                            percent >= 100
                                ? 'cpn-bg-amber-500'
                                : percent >= 75
                                ? 'cpn-bg-brand-500'
                                : 'cpn-bg-emerald-500'
                        }`}
                        style={{ width: `${percent}%` }}
                    />
                </div>
            )}
        </div>
    );
};
